import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAdminProfileAPI,
  updateAdminProfileAPI,
  uploadProfileImageAPI,
  deleteProfileImageAPI,
  changePasswordAPI,
  uploadCompanyLogoAPI,
  deleteCompanyLogoAPI,
} from "./Settingsapi";

// ✅ Get Admin Profile
export const getAdminProfile = createAsyncThunk(
  "settings/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAdminProfileAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch admin profile");
    }
  }
);

// ✅ Update Admin Profile
export const updateAdminProfile = createAsyncThunk(
  "settings/updateProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await updateAdminProfileAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update admin profile");
    }
  }
);

// ✅ Upload / Update Profile Image
export const uploadProfileImage = createAsyncThunk(
  "settings/uploadProfileImage",
  async (file, { rejectWithValue }) => {
    try {
      const response = await uploadProfileImageAPI(file);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to upload profile image");
    }
  }
);

// ✅ Delete Profile Image
export const deleteProfileImage = createAsyncThunk(
  "settings/deleteProfileImage",
  async (_, { rejectWithValue }) => {
    try {
      const response = await deleteProfileImageAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete profile image");
    }
  }
);

// ✅ Change Password
export const changePassword = createAsyncThunk(
  "settings/changePassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await changePasswordAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to change password");
    }
  }
);

// ✅ Upload Company Logo
export const uploadCompanyLogo = createAsyncThunk(
  "settings/uploadCompanyLogo",
  async (file, { rejectWithValue }) => {
    try {
      const response = await uploadCompanyLogoAPI(file);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to upload company logo");
    }
  }
);

// ✅ Delete Company Logo
export const deleteCompanyLogo = createAsyncThunk(
  "settings/deleteCompanyLogo",
  async (_, { rejectWithValue }) => {
    try {
      const response = await deleteCompanyLogoAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete company logo");
    }
  }
);

const initialState = {
  getAdminProfile: { data: null, loading: false, error: null },
  updateAdminProfile: { data: null, loading: false, error: null },
  uploadProfileImage: { data: null, loading: false, error: null },
  deleteProfileImage: { data: null, loading: false, error: null },
  changePassword: { data: null, loading: false, error: null },
  uploadCompanyLogo: { data: null, loading: false, error: null },
  deleteCompanyLogo: { data: null, loading: false, error: null },
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    clearGetAdminProfile: (state) => {
      state.getAdminProfile.data = null;
      state.getAdminProfile.error = null;
    },
    clearUpdateAdminProfile: (state) => {
      state.updateAdminProfile.data = null;
      state.updateAdminProfile.error = null;
    },
    clearUploadProfileImage: (state) => {
      state.uploadProfileImage.data = null;
      state.uploadProfileImage.error = null;
    },
    clearDeleteProfileImage: (state) => {
      state.deleteProfileImage.data = null;
      state.deleteProfileImage.error = null;
    },
    clearChangePassword: (state) => {
      state.changePassword.data = null;
      state.changePassword.error = null;
    },
    clearUploadCompanyLogo: (state) => {
      state.uploadCompanyLogo.data = null;
      state.uploadCompanyLogo.error = null;
    },
    clearDeleteCompanyLogo: (state) => {
      state.deleteCompanyLogo.data = null;
      state.deleteCompanyLogo.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Admin Profile
      .addCase(getAdminProfile.pending, (state) => {
        state.getAdminProfile.loading = true;
        state.getAdminProfile.error = null;
      })
      .addCase(getAdminProfile.fulfilled, (state, action) => {
        state.getAdminProfile.loading = false;
        state.getAdminProfile.data = action.payload?.data || action.payload;
      })
      .addCase(getAdminProfile.rejected, (state, action) => {
        state.getAdminProfile.loading = false;
        state.getAdminProfile.error = action.payload;
      })

      // Update Admin Profile
      .addCase(updateAdminProfile.pending, (state) => {
        state.updateAdminProfile.loading = true;
        state.updateAdminProfile.error = null;
      })
      .addCase(updateAdminProfile.fulfilled, (state, action) => {
        state.updateAdminProfile.loading = false;
        state.updateAdminProfile.data = action.payload;
      })
      .addCase(updateAdminProfile.rejected, (state, action) => {
        state.updateAdminProfile.loading = false;
        state.updateAdminProfile.error = action.payload;
      })

      // Upload Profile Image
      .addCase(uploadProfileImage.pending, (state) => {
        state.uploadProfileImage.loading = true;
        state.uploadProfileImage.error = null;
      })
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        state.uploadProfileImage.loading = false;
        state.uploadProfileImage.data = action.payload;
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.uploadProfileImage.loading = false;
        state.uploadProfileImage.error = action.payload;
      })

      // Delete Profile Image
      .addCase(deleteProfileImage.pending, (state) => {
        state.deleteProfileImage.loading = true;
        state.deleteProfileImage.error = null;
      })
      .addCase(deleteProfileImage.fulfilled, (state, action) => {
        state.deleteProfileImage.loading = false;
        state.deleteProfileImage.data = action.payload;
      })
      .addCase(deleteProfileImage.rejected, (state, action) => {
        state.deleteProfileImage.loading = false;
        state.deleteProfileImage.error = action.payload;
      })

      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.changePassword.loading = true;
        state.changePassword.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.changePassword.loading = false;
        state.changePassword.data = action.payload;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.changePassword.loading = false;
        state.changePassword.error = action.payload;
      })

      // Upload Company Logo
      .addCase(uploadCompanyLogo.pending, (state) => {
        state.uploadCompanyLogo.loading = true;
        state.uploadCompanyLogo.error = null;
      })
      .addCase(uploadCompanyLogo.fulfilled, (state, action) => {
        state.uploadCompanyLogo.loading = false;
        state.uploadCompanyLogo.data = action.payload;
      })
      .addCase(uploadCompanyLogo.rejected, (state, action) => {
        state.uploadCompanyLogo.loading = false;
        state.uploadCompanyLogo.error = action.payload;
      })

      // Delete Company Logo
      .addCase(deleteCompanyLogo.pending, (state) => {
        state.deleteCompanyLogo.loading = true;
        state.deleteCompanyLogo.error = null;
      })
      .addCase(deleteCompanyLogo.fulfilled, (state, action) => {
        state.deleteCompanyLogo.loading = false;
        state.deleteCompanyLogo.data = action.payload;
      })
      .addCase(deleteCompanyLogo.rejected, (state, action) => {
        state.deleteCompanyLogo.loading = false;
        state.deleteCompanyLogo.error = action.payload;
      });
  },
});

export const {
  clearGetAdminProfile,
  clearUpdateAdminProfile,
  clearUploadProfileImage,
  clearDeleteProfileImage,
  clearChangePassword,
  clearUploadCompanyLogo,
  clearDeleteCompanyLogo,
} = settingsSlice.actions;

export default settingsSlice.reducer;